/**
 * Deduplication module for preventing duplicate event tracking
 * Implements property-based and time-based deduplication strategies
 */

import { log, warn } from './logger.js';

const DEDUP_STORAGE_KEY = 'rbly_dedup_cache';

// Default configuration
const DEFAULT_CONFIG = {
  enabled: true,
  idempotencyKeys: ['orderId', 'transactionId', 'email', 'leadId'],
  timeWindow: 10000, // 10 seconds for time-based fallback
  retention: 7 * 24 * 60 * 60 * 1000 // 7 days
};

/**
 * Get deduplication cache from localStorage
 * Cache structure: { eventName: { uniqueId: timestamp, ... }, ... }
 * @returns {Object} The dedup cache
 */
function getDedupCache() {
  try {
    const cached = localStorage.getItem(DEDUP_STORAGE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (error) {
    warn('[Rebrandly SDK] Error reading dedup cache:', error);
    return {};
  }
}

/**
 * Save deduplication cache to localStorage
 * @param {Object} cache - The cache to save
 * @returns {boolean} true if successful
 */
function saveDedupCache(cache) {
  try {
    localStorage.setItem(DEDUP_STORAGE_KEY, JSON.stringify(cache));
    return true;
  } catch (error) {
    warn('[Rebrandly SDK] Error saving dedup cache:', error);
    return false;
  }
}

/**
 * Clear expired entries from the dedup cache
 * @param {number} retention - Retention period in milliseconds
 * @returns {number} Number of entries removed
 */
function cleanupExpiredEntries(retention = DEFAULT_CONFIG.retention) {
  try {
    const cache = getDedupCache();
    const now = Date.now();
    let removedCount = 0;

    Object.keys(cache).forEach(eventName => {
      const eventCache = cache[eventName];

      Object.keys(eventCache).forEach(key => {
        const timestamp = eventCache[key];

        // Remove if older than retention period
        if (now - timestamp > retention) {
          delete eventCache[key];
          removedCount++;
        }
      });

      // Remove event entry if empty
      if (Object.keys(eventCache).length === 0) {
        delete cache[eventName];
      }
    });

    if (removedCount > 0) {
      saveDedupCache(cache);
      log(`[Rebrandly SDK] Cleaned up ${removedCount} expired dedup entries`);
    }

    return removedCount;
  } catch (error) {
    warn('[Rebrandly SDK] Error cleaning up dedup cache:', error);
    return 0;
  }
}

/**
 * Extract idempotency key from event properties
 * @param {Object} properties - Event properties
 * @param {Array<string>} idempotencyKeys - List of property names to check
 * @returns {string|null} The idempotency key value if found
 */
function extractIdempotencyKey(properties, idempotencyKeys) {
  if (!properties || typeof properties !== 'object') {
    return null;
  }

  // Check each configured idempotency key
  for (const key of idempotencyKeys) {
    if (properties[key] !== undefined && properties[key] !== null) {
      // Convert to string to ensure consistency
      return String(properties[key]);
    }
  }

  return null;
}

/**
 * Generate a simple fingerprint from event properties for time-based deduplication.
 * Produces a deterministic numeric string using djb2 hash.
 * @param {Object} properties - Event properties
 * @returns {string} A fingerprint string, or empty string if no properties
 */
function generatePropertyFingerprint(properties) {
  if (!properties || typeof properties !== 'object') {
    return '';
  }

  const keys = Object.keys(properties).sort();
  if (keys.length === 0) {
    return '';
  }

  const raw = keys.map(k => `${k}=${String(properties[k])}`).join('&');
  let hash = 5381;
  for (let i = 0; i < raw.length; i++) {
    hash = ((hash << 5) + hash) + raw.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return String(Math.abs(hash));
}

/**
 * Check if an event is a duplicate
 * Uses property-based deduplication (idempotency keys) or time-based fallback
 *
 * @param {string} event - Event name
 * @param {Object} properties - Event properties
 * @param {Object} config - Deduplication configuration
 * @returns {Object} Result object with {isDuplicate: boolean, reason: string|null, key: string|null}
 */
function isDuplicateEvent(event, properties = {}, config = DEFAULT_CONFIG) {
  if (!config.enabled) {
    return { isDuplicate: false, reason: null, key: null };
  }

  const cache = getDedupCache();
  const now = Date.now();

  // Initialize event cache if not exists
  if (!cache[event]) {
    cache[event] = {};
  }

  const eventCache = cache[event];

  // Strategy 1: Property-based deduplication (primary)
  const idempotencyKey = extractIdempotencyKey(properties, config.idempotencyKeys);

  if (idempotencyKey) {
    const cacheKey = `prop:${idempotencyKey}`;

    if (eventCache[cacheKey]) {
      const lastTracked = eventCache[cacheKey];
      const timeSinceLastTracked = now - lastTracked;

      // Check if within retention period
      if (timeSinceLastTracked <= config.retention) {
        return {
          isDuplicate: true,
          reason: 'property_based',
          key: idempotencyKey,
          lastTracked: new Date(lastTracked).toISOString(),
          timeSinceLastTracked
        };
      } else {
        // Expired, can be tracked again
        delete eventCache[cacheKey];
      }
    }

    // Not a duplicate, mark as tracked
    eventCache[cacheKey] = now;
    saveDedupCache(cache);

    return { isDuplicate: false, reason: null, key: idempotencyKey };
  }

  // Strategy 2: Time-based fallback (for events without unique IDs)
  const fingerprint = generatePropertyFingerprint(properties);
  const timeBasedKey = fingerprint ? `time:${fingerprint}` : 'time:last';

  if (eventCache[timeBasedKey]) {
    const lastTracked = eventCache[timeBasedKey];
    const timeSinceLastTracked = now - lastTracked;

    // Check if within time window
    if (timeSinceLastTracked <= config.timeWindow) {
      return {
        isDuplicate: true,
        reason: 'time_based',
        key: null,
        lastTracked: new Date(lastTracked).toISOString(),
        timeSinceLastTracked
      };
    }
  }

  // Not a duplicate, mark as tracked
  eventCache[timeBasedKey] = now;
  saveDedupCache(cache);

  return { isDuplicate: false, reason: null, key: null };
}

/**
 * Clear all deduplication cache (useful for testing)
 * @returns {boolean} true if successful
 */
function clearDedupCache() {
  try {
    localStorage.removeItem(DEDUP_STORAGE_KEY);
    log('[Rebrandly SDK] Deduplication cache cleared');
    return true;
  } catch (error) {
    warn('[Rebrandly SDK] Error clearing dedup cache:', error);
    return false;
  }
}

/**
 * Get deduplication statistics (for debugging)
 * @returns {Object} Statistics about the dedup cache
 */
function getDedupStats() {
  try {
    const cache = getDedupCache();
    const stats = {
      totalEvents: 0,
      totalEntries: 0,
      events: {}
    };

    Object.keys(cache).forEach(eventName => {
      const eventCache = cache[eventName];
      const entryCount = Object.keys(eventCache).length;

      stats.totalEvents++;
      stats.totalEntries += entryCount;
      stats.events[eventName] = {
        entries: entryCount,
        oldestEntry: Math.min(...Object.values(eventCache)),
        newestEntry: Math.max(...Object.values(eventCache))
      };
    });

    return stats;
  } catch (error) {
    warn('[Rebrandly SDK] Error getting dedup stats:', error);
    return null;
  }
}

export {
  isDuplicateEvent,
  clearDedupCache,
  cleanupExpiredEntries,
  getDedupStats,
  extractIdempotencyKey,
  generatePropertyFingerprint,
  DEFAULT_CONFIG,
  DEDUP_STORAGE_KEY
};
